// SUPORTE: chamados abertos no Disk e atendidos no Jira Service Management.
// O back é a única fronteira com a Atlassian — o front nunca fala com o Jira,
// nem para carregar a imagem de um anexo (ver `supportAttachmentUrl`).
//
// A feature é ligada por org, pelas envs do Jira no back: `getSupportConfig` é
// o gate fail-closed que a sidebar, a Ajuda, a lista e a tela do chamado
// consultam antes de oferecer qualquer coisa.
//
// Três formas de escrever, e nenhuma é intercambiável: abrir (POST, JSON),
// responder só com texto (POST .../comments, JSON) e responder com arquivo
// (POST .../comments-with-files, multipart). O multipart usa `fetch` cru
// porque `api()` serializa JSON — e não usa o `apiUpload` de ./client.js porque
// aquele devolve o JSON da resposta e este endpoint não tem corpo a ler.
import { api, API_BASE_URL, toApiError } from "./client.js";

export interface SupportConfig {
  enabled: boolean;
}

export interface SupportTicketSummary {
  id: string;
  subject: string;
  status: string | null;
  source: "help" | "assistant";
  createdAt: string;
  lastActivityAt: string | null;
  // O back já filtra por papel; `mine` só serve para a lista do gestor dizer
  // quais chamados são dele e quais são da equipe.
  mine: boolean;
  author: string | null;
  // Encerramento pelo nosso lado (POST .../close). Independente do status do
  // Jira: um chamado pode estar "Em atendimento" lá e fechado aqui.
  closedAt: string | null;
  closedBy: string | null;
}

export interface SupportAttachment {
  id: string;
  filename: string;
  mimeType: string;
  size: number;
}

export interface SupportMessage {
  id: string;
  author: string;
  text: string;
  createdAt: string;
  attachments: SupportAttachment[];
}

export interface SupportTicketDetail {
  id: string;
  subject: string;
  jiraIssueKey: string;
  status: string;
  messages: SupportMessage[];
  author: string | null;
  createdAt: string;
  // Data do comentário público mais recente — null quando o chamado ainda
  // não recebeu nenhum.
  lastActivityAt: string | null;
  source: "help" | "assistant";
  closedAt: string | null;
  closedBy: string | null;
  // O card foi apagado no Jira: o cabeçalho ainda vale (é nosso), `messages`
  // vem vazio e responder deixa de ser possível.
  removed: boolean;
}

export async function getSupportConfig(): Promise<SupportConfig> {
  const { data, error } = await api<SupportConfig>("/v1/support/config");
  if (error) throw toApiError("GET /v1/support/config", error);
  return data;
}

export async function listSupportTickets(): Promise<SupportTicketSummary[]> {
  const { data, error } = await api<SupportTicketSummary[]>("/v1/support/tickets");
  if (error) throw toApiError("GET /v1/support/tickets", error);
  return data;
}

export async function getSupportTicket(id: string): Promise<SupportTicketDetail> {
  const { data, error } = await api<SupportTicketDetail>(`/v1/support/tickets/${id}`);
  if (error) throw toApiError(`GET /v1/support/tickets/${id}`, error);
  return data;
}

export async function closeSupportTicket(id: string): Promise<void> {
  const { error } = await api(`/v1/support/tickets/${id}/close`, { method: "POST" });
  if (error) throw toApiError(`POST /v1/support/tickets/${id}/close`, error);
}

export async function reopenSupportTicket(id: string): Promise<void> {
  const { error } = await api(`/v1/support/tickets/${id}/reopen`, { method: "POST" });
  if (error) throw toApiError(`POST /v1/support/tickets/${id}/reopen`, error);
}

export async function createSupportTicket(input: {
  subject: string;
  description: string;
  source: "help" | "assistant";
  conversationId?: string;
  attachConversation?: boolean;
  route?: string;
  // Versão do front no momento da abertura — ajuda o suporte a descartar bug
  // já corrigido em um deploy mais novo. Usa a identidade do bundle atual.
  frontVersion?: string;
}): Promise<{ id: string; jiraIssueKey: string }> {
  const { data, error } = await api<{ id: string; jiraIssueKey: string }>("/v1/support/tickets", {
    method: "POST",
    body: input,
  });
  if (error) throw toApiError("POST /v1/support/tickets", error);
  return data;
}

export async function commentSupportTicket(id: string, text: string): Promise<void> {
  const { error } = await api<{ ok: boolean }>(`/v1/support/tickets/${id}/comments`, {
    method: "POST",
    body: { text },
  });
  if (error) throw toApiError(`POST /v1/support/tickets/${id}/comments`, error);
}

// Envio de anexo é multipart: NÃO passa pelo `api()` (que serializa JSON).
// O `fetch` cru monta o boundary sozinho — definir Content-Type à mão quebra.
export async function uploadTicketFiles(
  id: string,
  input: { files: File[]; text?: string },
): Promise<void> {
  const form = new FormData();
  for (const file of input.files) form.append("files", file);
  if (input.text?.trim()) form.append("text", input.text.trim());

  const response = await fetch(`${API_BASE_URL}/v1/support/tickets/${id}/comments-with-files`, {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!response.ok) {
    const corpo = (await response.json().catch(() => ({}))) as { error?: string };
    throw toApiError(`POST /v1/support/tickets/${id}/comments-with-files`, {
      status: response.status,
      error: corpo.error,
    });
  }
}

// A imagem é servida pelo BACK, com a sessão do usuário — o navegador não fala
// com o Jira. Por isso é URL, não blob: o <img> carrega direto, com cookie.
export function supportAttachmentUrl(ticketId: string, attachmentId: string): string {
  return `${API_BASE_URL}/v1/support/tickets/${ticketId}/attachments/${attachmentId}`;
}
